import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import PageHeader from '../../components/common/PageHeader';
import SourceCombobox from '../../components/common/SourceCombobox';
import { MT5AccountUser } from '../../utils/types';
import {
  getCommentValidationMessage,
  MAX_COMMENT_LENGTH,
  MAX_TRANSACTION_AMOUNT,
  MIN_COMMENT_LENGTH,
  MIN_TRANSACTION_AMOUNT,
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
} from '../../utils/validationMessages';
import { mt5API } from '../../services/api';
import LoaderOverlay from '../../components/common/LoaderOverlay';
import { USER_ROUTES } from '../../constants/routeConstants';

const Deposit = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const passedLogin =
    location.state?.login ||
    location.state?.loginId ||
    searchParams.get('login') ||
    searchParams.get('loginId') ||
    '';

  const [accounts, setAccounts] = useState<MT5AccountUser[]>([]);
  const [selectedLogin, setSelectedLogin] = useState<string>(
    passedLogin ? String(passedLogin) : ''
  );
  const [amount, setAmount] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    login?: string;
    amount?: string;
    comment?: string;
  }>({});

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (passedLogin) {
      setSelectedLogin(String(passedLogin));
    }
  }, [passedLogin]);

  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await mt5API.getUsers();
      const data = response?.data || response;
      if (Array.isArray(data) && data.length > 0) {
        const mapped = data.map((acc: any) => ({
          ...acc,
          Login: acc.mt5Login ?? acc.Login ?? acc.id,
          Name: acc.name ?? acc.Name ?? 'Account',
          Group: acc.group ?? acc.Group ?? '-',
          Balance: Number(acc.balance ?? acc.Balance ?? 0),
          Leverage: acc.leverage ?? acc.Leverage,
          Credit: Number(acc.credit ?? acc.Credit ?? 0),
        }));
        setAccounts(mapped);
        if (passedLogin) {
          setSelectedLogin(String(passedLogin));
        }
      }
    } catch (error) {
      console.error('Error fetching accounts for deposit:', error);
      toast.error('Failed to load trading accounts.');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const validate = () => {
    const newErrors: { login?: string; amount?: string; comment?: string } = {};
    const numericAmount = Number(amount);
    const commentError = getCommentValidationMessage(comment);

    if (!selectedLogin) {
      newErrors.login = 'Please select a trading account.';
    }

    if (!amount || isNaN(numericAmount)) {
      newErrors.amount = `Please enter a valid deposit amount between $${MIN_TRANSACTION_AMOUNT} and $${MAX_TRANSACTION_AMOUNT}.`;
    } else if (numericAmount < MIN_TRANSACTION_AMOUNT) {
      newErrors.amount = `Deposit amount must be at least $${MIN_TRANSACTION_AMOUNT}.`;
    } else if (numericAmount > MAX_TRANSACTION_AMOUNT) {
      newErrors.amount = `Deposit amount cannot exceed $${MAX_TRANSACTION_AMOUNT}.`;
    }

    if (commentError) {
      newErrors.comment = commentError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        login: isNaN(Number(selectedLogin))
          ? selectedLogin
          : Number(selectedLogin),
        amount: Number(amount),
        comment: comment.trim() || 'Client deposit',
      };

      const response = await mt5API.deposit(payload);
      if (response?.success !== false) {
        toast.success(response?.message || 'Deposit processed successfully!');
        setAmount('');
        setComment('Client deposit');
        fetchAccounts();
      } else {
        toast.error(response?.message || 'Failed to process deposit.');
      }
    } catch (error: any) {
      console.error('Error executing deposit:', error);
      toast.error(
        error?.response?.data?.message || 'Error processing deposit request.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const selectedAccountObj = accounts.find(
    acc => String(acc.Login || (acc as any).mt5Login) === String(selectedLogin)
  );

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Deposit Funds"
          description="Add funds to your trading wallet securely."
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Deposit Details</CardTitle>
          </CardHeader>
          <CardContent>
            <LoaderOverlay
              isLoading={loadingAccounts}
              loadingText="Loading trading accounts..."
            >
              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="account">
                      Select Trading Account{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    <SourceCombobox
                      id="account"
                      disabled={!!passedLogin}
                      items={accounts.map(
                        acc =>
                          `${acc.Login || (acc as any).mt5Login} (${acc.Name || (acc as any).name || 'Account'})`
                      )}
                      placeholder="Select trading account"
                      value={
                        selectedAccountObj
                          ? `${selectedAccountObj.Login || (selectedAccountObj as any).mt5Login} (${selectedAccountObj.Name || (selectedAccountObj as any).name || 'Account'})`
                          : selectedLogin
                            ? String(selectedLogin)
                            : ''
                      }
                      onChange={val => {
                        if (passedLogin) return;
                        const loginNum = val.split(' ')[0];
                        setSelectedLogin(loginNum);
                        if (errors.login)
                          setErrors(prev => ({ ...prev, login: undefined }));
                      }}
                      emptyText="No trading accounts found."
                    />
                    {errors.login && (
                      <p className="text-xs text-red-500 font-medium">
                        {errors.login}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="amount">
                      Amount ($) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="amount"
                      type="number"
                      min={MIN_TRANSACTION_AMOUNT}
                      max={MAX_TRANSACTION_AMOUNT}
                      step="0.01"
                      placeholder="Enter deposit amount"
                      value={amount}
                      onChange={e => {
                        setAmount(e.target.value);
                        if (errors.amount)
                          setErrors(prev => ({ ...prev, amount: undefined }));
                      }}
                      onKeyDown={preventInvalidNumberInput}
                      onPaste={preventInvalidNumberPaste}
                      onInvalid={event => event.preventDefault()}
                    />
                    {errors.amount && (
                      <p className="text-xs text-red-500 font-medium">
                        {errors.amount}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comment">
                    Comment <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="comment"
                    placeholder="add comment...."
                    minLength={MIN_COMMENT_LENGTH}
                    maxLength={MAX_COMMENT_LENGTH}
                    value={comment}
                    onChange={e => {
                      setComment(e.target.value);
                      if (errors.comment)
                        setErrors(prev => ({ ...prev, comment: undefined }));
                    }}
                  />
                  {errors.comment && (
                    <p className="text-xs text-red-500 font-medium">
                      {errors.comment}
                    </p>
                  )}
                </div>

                {selectedAccountObj && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 rounded-md bg-muted/40 border text-xs">
                    <div>
                      <span className="text-muted-foreground block">
                        Current Balance:
                      </span>
                      <span className="font-semibold text-sm">
                        $
                        {(selectedAccountObj.Balance || 0).toLocaleString(
                          'en-US',
                          { minimumFractionDigits: 2 }
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">
                        Account Group:
                      </span>
                      <span className="font-medium text-sm">
                        {selectedAccountObj.Group || '-'}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (!passedLogin) {
                        setSelectedLogin('');
                      }
                      setAmount('');
                      setComment('Client deposit');
                      setErrors({});
                    }}
                    disabled={submitting}
                  >
                    Reset
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(USER_ROUTES.USER_WALLET)}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Processing...' : 'Deposit Funds'}
                  </Button>
                </div>
              </form>
            </LoaderOverlay>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Funding Limits & Policy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <ul className="space-y-2 list-disc list-inside">
              <li>
                Deposit amount range:{' '}
                <span className="font-medium text-foreground">
                  ${MIN_TRANSACTION_AMOUNT} - ${MAX_TRANSACTION_AMOUNT}
                </span>
              </li>
              <li>Instant account credit upon processing.</li>
              <li>No internal transaction fees.</li>
            </ul>
            <div className="border-t pt-4 space-y-1">
              <p className="font-medium text-foreground text-sm">
                Risk Warning
              </p>
              <p>
                Trading Forex and CFDs involves a high level of risk and may not
                be suitable for all investors. Never deposit money you cannot
                afford to lose.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Deposit;
